import * as React from "react";
import {useState} from "react";
import Card from "@mui/material/Card";
import OutlinedInput from "@mui/material/OutlinedInput";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Unstable_Grid2";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import {Autocomplete, TextField} from "@mui/material";
import Chip from "@mui/material/Chip";
import Plant, {PlantRegion} from "benchmarking-domain/plant";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import {FrameworkStatus} from "benchmarking-domain/framework";
import MenuItem from "@mui/material/MenuItem";
import {UserSelect} from "../../core/shared/user-select";
import {Country} from "currency-codes-ts/dist/types";
import _default from "currency-codes-ts";
import Stack from "@mui/system/Stack";
import {PlantArchiveButton} from "./plant-archive-confirmation";

const PlantArchiveButtonMemo = React.memo(PlantArchiveButton);

interface PlantFormProps {
  organisationId: string;
  plant?: Plant;
  onSubmit: (data: Plant) => Promise<void>;
}

export function PlantForm({organisationId, plant, onSubmit}: PlantFormProps): React.JSX.Element {
  const [prevPlant, setPrevPlant] = useState<Plant | undefined>(plant);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [name, setName] = useState<string>(plant?.name ?? "");
  const [region, setRegion] = useState<PlantRegion>(plant?.region ?? PlantRegion.WesternEurope);
  const [country, setCountry] = useState<Country>(plant?.country ?? _default.countries().sort((one, two) => (one < two ? -1 : 1))[0] as Country);
  const [technologies, setTechnologies] = useState<string[]>(plant?.technologies ?? []);
  const [managers, setManagers] = useState<string[]>(plant?.managers ?? []);

  if(prevPlant !== plant) {
    setPrevPlant(plant);
    setName(plant?.name ?? "");
    setRegion(plant?.region ?? PlantRegion.WesternEurope);
    setCountry(plant?.country ?? _default.countries().sort((one, two) => (one < two ? -1 : 1))[0] as Country);
    setManagers(plant?.managers ?? []);
  }

  const saveData = async ():Promise<void> => {
    setIsSaving(true);

    try {
      await onSubmit({ ...plant, id: plant?.id ?? "", name, region, country, technologies, managers });
      if(!plant) {
        resetState();
      }
    } finally {
      setIsSaving(false);
    }
  }

  const handleCountryChange = (event: SelectChangeEvent) => {
    setCountry(event.target.value as Country);
  };

  const resetState = () => {
    setName(plant?.name ?? "")
    setManagers(plant?.managers ?? []);
  }

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        await saveData();
      }}
    >
      <Card>
        <CardHeader subheader="The information can be edited" title={plant ? "Plant" : "New plant"}/>
        <Divider/>
        <CardContent>
          <Grid container spacing={3}>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Name</InputLabel>
                <OutlinedInput onChange={(e) => {
                  setName(e.target.value);
                }} value={name} label="Name" name="name"/>
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel>Region</InputLabel>
                <Select value={region} onChange={(e) => {
                  setRegion(e.target.value as PlantRegion);
                }}
                        label="Region" name="region" variant="outlined">
                  {Object.values(PlantRegion).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Country</InputLabel>
                <Select
                  label="Country"
                  id="country-select"
                  variant="outlined"
                  value={country}
                  onChange={handleCountryChange}
                  sx={{ color: "inherit" }}
                >
                  {_default.countries().sort((one, two) => (one < two ? -1 : 1)).map((countryRecord) => (
                    <MenuItem
                      key={countryRecord}
                      value={countryRecord}
                    >
                      {countryRecord}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <Autocomplete
                  multiple
                  id="technologies-filled"
                  options={[]}
                  freeSolo
                  autoSelect
                  value={technologies}
                  onChange={(e, value) => {
                    setTechnologies(value);
                  }}
                  renderTags={(value: string[], getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({index})}
                        key={option}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Technologies"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <UserSelect
                  id="managers-filled"
                  organisationId={organisationId}
                  label="Managers"
                  value={managers}
                  onChange={setManagers}
                />
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
        <Divider/>
        <CardActions sx={{justifyContent: 'flex-end'}}>
          <Stack direction="row" spacing={1}>
            {plant &&
              <PlantArchiveButtonMemo organisationId={organisationId} plantId={plant.id} />
            }
            <Button type="submit" variant="contained" disabled={isSaving}>{plant ? "Save details" : "Create new plant"}</Button>
          </Stack>
        </CardActions>
      </Card>
    </form>
  );
}
